import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const PIXEL_CONSENT_STORAGE_KEY = 'upreality_pixel_consent';
export const PIXEL_CONSENT_REOPEN_EVENT = 'upreality-reopen-pixel-consent';

// Permet de rouvrir la languette de consentement (retrait / modification du choix).
export function requestReopenPixelConsent() {
  try {
    window.dispatchEvent(new CustomEvent(PIXEL_CONSENT_REOPEN_EVENT));
  } catch (error) {
    // CustomEvent indisponible (très vieux navigateurs)
  }
}

// Repli mémoire : dans les navigateurs internes (Instagram/Facebook/TikTok),
// localStorage peut être éphémère ou lever une exception. On garde le choix en
// RAM pour fiabiliser le consentement au moins pendant la session en cours.
let inMemoryPixelConsent = null;

export function getStoredPixelConsent() {
  try {
    const value = window.localStorage.getItem(PIXEL_CONSENT_STORAGE_KEY);
    if (value != null) {
      return value;
    }
  } catch (error) {
    // localStorage indisponible : on retombe sur le repli mémoire.
  }
  return inMemoryPixelConsent;
}

// Lecture "persistée uniquement" (localStorage, sans repli mémoire) : sert à
// distinguer un choix explicite de l'utilisateur d'une acceptation auto non
// persistée (visite directe).
export function getPersistedPixelConsent() {
  try {
    return window.localStorage.getItem(PIXEL_CONSENT_STORAGE_KEY);
  } catch (error) {
    return null;
  }
}

export function setStoredPixelConsent(value, { persist = true } = {}) {
  inMemoryPixelConsent = value;
  if (!persist) {
    // Consentement volontairement non persisté (ex. visite directe) :
    // valable pour la session en cours uniquement, via le repli mémoire.
    return;
  }
  try {
    window.localStorage.setItem(PIXEL_CONSENT_STORAGE_KEY, value);
  } catch (error) {
    // localStorage indisponible (navigation privée, in-app browser…) :
    // le repli mémoire ci-dessus conserve le choix pour la session.
  }
}

function CookieIcon({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M12 2.5c.4 0 .8.02 1.19.07a1.6 1.6 0 0 0 1.7 2.2 1.6 1.6 0 0 0 1.53 1.94 1.6 1.6 0 0 0 2.02 1.6A9.5 9.5 0 1 1 12 2.5Z"
        stroke="#5b4a3f"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="10" r="1" fill="#5b4a3f" />
      <circle cx="14.5" cy="13.5" r="1.1" fill="#5b4a3f" />
      <circle cx="9.5" cy="15" r="0.9" fill="#5b4a3f" />
    </svg>
  );
}

function PixelConsentOverlay({ sizeMultiplier = 1, onAccept, onRefuse, onClose, hasMeta = true, hasTikTok = false, isReopen = false }) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  // Adapte le libellé du détail selon le(s) pixel(s) réellement installé(s).
  const bothPixels = hasMeta && hasTikTok;
  const pixelsLabel = bothPixels
    ? t('ConsentPixelBoth')
    : hasTikTok
      ? t('ConsentPixelTikTok')
      : t('ConsentPixelMeta');
  const controllersLabel = bothPixels
    ? t('ConsentControllerBoth')
    : hasTikTok
      ? t('ConsentControllerTikTok')
      : t('ConsentControllerMeta');

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 3000,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        background: 'rgba(0, 0, 0, 0.45)',
        pointerEvents: 'auto',
      }}
      onClick={isReopen ? onClose : undefined}
      role={isReopen ? 'presentation' : undefined}
    >
      <div
        style={{
          background: '#fbf5ec',
          borderTop: '1px solid #e8c9c0',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.12)',
          padding: `${20 * sizeMultiplier}px ${22 * sizeMultiplier}px`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 * sizeMultiplier }}>
          <div style={{ flexShrink: 0, marginTop: 2 }}>
            <CookieIcon size={26 * sizeMultiplier} />
          </div>
          <p
            style={{
              margin: 0,
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic',
              fontSize: 16 * sizeMultiplier,
              lineHeight: 1.45,
              color: '#2e2a26',
            }}
          >
            {t('ConsentMessage')}{' '}
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: '#2e2a26',
                textDecoration: 'underline',
                fontStyle: 'italic',
                fontFamily: 'inherit',
                fontSize: 'inherit',
              }}
            >
              {showDetails ? t('ConsentLess') : t('ConsentLearnMore')}
            </button>
          </p>
        </div>

        {showDetails && (
          <div
            style={{
              marginTop: 14 * sizeMultiplier,
              paddingTop: 14 * sizeMultiplier,
              borderTop: '1px dashed #e0c4b8',
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 14 * sizeMultiplier,
              lineHeight: 1.5,
              color: '#3a332e',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8 * sizeMultiplier, fontStyle: 'italic' }}>
              {t('ConsentDetailsTitle')}
            </div>
            <p style={{ margin: `0 0 ${6 * sizeMultiplier}px` }}>{t('ConsentDetailsPurpose', { pixels: pixelsLabel })}</p>
            <p style={{ margin: `0 0 ${6 * sizeMultiplier}px` }}>{t('ConsentDetailsData')}</p>
            <p style={{ margin: `0 0 ${6 * sizeMultiplier}px` }}>{t('ConsentDetailsWho', { controllers: controllersLabel })}</p>
            <p style={{ margin: 0 }}>{t('ConsentDetailsChoice')}</p>
          </div>
        )}

        {isReopen && (
          <p
            style={{
              margin: `${14 * sizeMultiplier}px 0 0`,
              paddingLeft: (26 + 12) * sizeMultiplier,
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 14 * sizeMultiplier,
              lineHeight: 1.45,
              color: '#3d6b4f',
              fontWeight: 600,
              fontStyle: 'italic',
            }}
          >
            {t('ConsentCurrentAccepted')}
          </p>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 12 * sizeMultiplier,
            marginTop: 18 * sizeMultiplier,
          }}
        >
          <button
            type="button"
            onClick={onRefuse}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6b5a4f',
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontWeight: 600,
              fontSize: 14 * sizeMultiplier,
              padding: `${12 * sizeMultiplier}px ${16 * sizeMultiplier}px`,
            }}
          >
            {t('ConsentRefuse')}
          </button>
          <button
            type="button"
            onClick={onAccept}
            style={isReopen ? {
              background: 'transparent',
              color: '#8a7a6f',
              border: '1px solid #d4c4b8',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontWeight: 600,
              fontSize: 13 * sizeMultiplier,
              borderRadius: 999,
              padding: `${10 * sizeMultiplier}px ${22 * sizeMultiplier}px`,
            } : {
              background: '#a01c1c',
              color: '#ffffff',
              border: 'none',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontWeight: 700,
              fontSize: 14 * sizeMultiplier,
              borderRadius: 999,
              padding: `${14 * sizeMultiplier}px ${34 * sizeMultiplier}px`,
            }}
          >
            {t('ConsentAccept')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PixelConsentOverlay;
